import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { api } from './api';

const LandingPage = ({ onNavigate }) => {
  const navigate = useNavigate();
  const [showPricing, setShowPricing] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [pricingError, setPricingError] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Handle Get Started - Navigate to HOA setup flow
  const handleGetStarted = () => {
    // Use the onNavigate callback to go to setup flow
    onNavigate('setup-hoa');
  };

  // Fetch pricing from backend when user opens the pricing section
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setLoadingPricing(true);
        setPricingError(null);
        const res = await axios.get('/api/subscriptions/pricing');
        setPricing(res.data);
      } catch (e) {
        console.error('Failed to load pricing', e);
        setPricingError('Unable to load pricing. Showing sample pricing.');
      } finally {
        setLoadingPricing(false);
      }
    };
    if (showPricing && !pricing) {
      fetchPricing();
    }
  }, [showPricing, pricing]);

  const pricingTiers = useMemo(() => {
    if (!pricing) {
      // Fallback sample tiers (matches signup flow exactly)
      return [
        {
          id: 'basic',
          name: 'Basic Community',
          homes: 'Up to 100 homes',
          yearly_price: 399,
          monthly_price: 35,
          features: [
            'Basic document storage (15GB)',
            'User management & board tools',
            'Payment processing for dues',
            'Community calendar',
            'Basic financial tracking',
            'HOA Track',
            'Newsletter and announcements',
            'HOA Smart Pro',
            'External free users (attorney, staff)',
          ],
        },
        {
          id: 'standard',
          name: 'Standard Community',
          homes: 'Up to 300 homes',
          yearly_price: 499,
          monthly_price: 45,
          popular: true,
          features: [
            'Everything in Basic',
            'Advanced communications (25GB)',
            'Event management system',
            'HOA Files Pro',
            'Document templates',
            'Custom branding',
            'Priority support',
          ],
        },
        {
          id: 'premium',
          name: 'Premium Community',
          homes: 'Up to 1000 homes',
          yearly_price: 1020,
          monthly_price: 85,
          features: [
            'Everything in Standard',
            'Guest Management',
            'Security and maintenance users',
            'Advanced analytics (100GB)',
            'Multiple board groups',
            'Dedicated support',
            'Custom integrations',
          ],
        },
      ];
    }

    const plans = pricing?.plans || {};
    return Object.keys(plans).map((key) => ({ id: key, ...plans[key] }));
  }, [pricing]);

  const addons = useMemo(() => {
    if (!pricing) return null;
    const raw = pricing.addons || {};
    return Object.keys(raw).map((key) => ({ id: key, ...raw[key] }));
  }, [pricing]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <img 
                src="/hoa-logo-icon.png" 
                alt="HOA Manager Pro" 
                className="h-16 w-16 object-contain"
              />
              <div className="ml-4 flex flex-col">
                <span className="text-3xl font-bold tracking-wide" style={{ color: '#5A2D82', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                  HOA
                </span>
                <span className="text-lg font-bold tracking-wide leading-none" style={{ color: '#5A2D82', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                  MANAGER PRO
                </span>
              </div>
            </div>
            <div className="hidden md:flex space-x-8">
              <button onClick={() => navigate('/features')} className="text-gray-600 hover:text-indigo-600 px-3 py-2 text-sm font-medium">
                Features
              </button>
              <button onClick={() => navigate('/security')} className="text-gray-600 hover:text-indigo-600 px-3 py-2 text-sm font-medium">
                Security
              </button>
              <button onClick={() => navigate('/about')} className="text-gray-600 hover:text-indigo-600 px-3 py-2 text-sm font-medium">
                About
              </button>
              <button onClick={() => navigate('/contact')} className="text-gray-600 hover:text-indigo-600 px-3 py-2 text-sm font-medium">
                Contact
              </button>
              <button
                onClick={() => {
                  setShowPricing(true);
                  setTimeout(() => {
                    const pricingSection = document.getElementById('pricing-section');
                    if (pricingSection) {
                      pricingSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 100);
                }}
                className="text-gray-600 hover:text-indigo-600 px-3 py-2 text-sm font-medium"
              >
                Pricing
              </button>
              <button
                onClick={() => onNavigate('existing-login')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Sign In
              </button>
            </div>
            
            {/* Mobile hamburger menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-2 rounded-md text-gray-600 hover:text-indigo-600 hover:bg-gray-100"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile Navigation Menu */}
        {showMobileMenu && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <button
                onClick={() => {
                  navigate('/features');
                  setShowMobileMenu(false);
                }}
                className="block w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
              >
                Features
              </button>
              <button
                onClick={() => {
                  navigate('/security');
                  setShowMobileMenu(false);
                }}
                className="block w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
              >
                Security
              </button>
              <button
                onClick={() => {
                  navigate('/about');
                  setShowMobileMenu(false);
                }}
                className="block w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
              >
                About
              </button>
              <button
                onClick={() => {
                  navigate('/contact');
                  setShowMobileMenu(false);
                }}
                className="block w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
              >
                Contact
              </button>
              <button
                onClick={() => {
                  setShowPricing(true);
                  setShowMobileMenu(false);
                  setTimeout(() => {
                    const pricingSection = document.getElementById('pricing-section');
                    if (pricingSection) {
                      pricingSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 100);
                }}
                className="block w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
              >
                Pricing
              </button>
              <button
                onClick={() => {
                  onNavigate('existing-login');
                  setShowMobileMenu(false);
                }}
                className="block w-full text-left px-3 py-2 text-base font-medium bg-indigo-600 text-white hover:bg-indigo-700 rounded-md mt-2"
              >
                Sign In
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Simplify, DIY Your HOA Management</h1>
            <p className="text-xl md:text-2xl mb-8 text-indigo-100">Everything your community needs in one powerful platform</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center relative z-10">
              <button onClick={() => onNavigate('setup-hoa')} className="bg-white text-indigo-600 hover:bg-gray-50 px-8 py-4 rounded-lg text-lg font-semibold shadow-lg relative z-20">
                Start Your HOA
              </button>
              <button 
                onClick={() => {
                  console.log('🔴 COMMUNITY MEMBER LOGIN BUTTON CLICKED!');
                  console.log('🔴 onNavigate function:', typeof onNavigate);
                  onNavigate('existing-login');
                }} 
                className="border-2 border-white text-white hover:bg-white hover:text-indigo-600 px-8 py-4 rounded-lg text-lg font-semibold relative z-20"
              >
                Community Member Login
              </button>
            </div>
            {/* Load Quote Option */}
            <div className="mt-6 text-center relative z-10">
              <button onClick={() => onNavigate('load-quote')} className="text-indigo-200 hover:text-white underline text-sm font-medium">
                📋 Have a saved quote? Continue your setup
              </button>
            </div>
          </div>
        </div>
        {/* Decorative background */}
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L1440 120L1440 0C1440 0 1140 80 720 80C300 80 0 0 0 0V120Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Everything Your HOA Needs</h2>
            <p className="text-xl text-gray-600">Streamline operations, improve communication, and keep residents happy</p>
          </div>
          {/* ... existing feature cards remain unchanged ... */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Cards left as-is for brevity */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4">User Management</h3>
              <p className="text-gray-600">Board member controls, homeowner verification, and role-based access for secure community management.</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4">Payment Processing</h3>
              <p className="text-gray-600">Secure dues collection, fine management, and automated payment reminders with detailed tracking.</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4">Document Management</h3>
              <p className="text-gray-600">Secure storage for meeting minutes, ARC documents, and HOA rules with easy search functionality.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {showPricing && (
        <section id="pricing-section" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Simple, Transparent Pricing</h2>
              <p className="text-lg text-gray-600">Yearly vs Monthly options for each plan. Add-ons are available yearly/monthly.</p>
              {loadingPricing && <p className="text-sm text-gray-500 mt-2">Loading latest pricing...</p>}
              {pricingError && <p className="text-sm text-yellow-700 mt-2">{pricingError}</p>}
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {pricingTiers.map((tier) => (
                <div key={tier.id} className={`rounded-2xl shadow-xl ${tier.popular ? 'border-2 border-indigo-600 relative' : 'border border-gray-200'}`}>
                  {tier.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-semibold">Most Popular</span>
                    </div>
                  )}
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                    <p className="text-gray-600 mb-4">{tier.homes}</p>
                    <div className="mb-6 grid grid-cols-2 gap-2">
                      <div className="text-center p-3 rounded-lg bg-gray-50">
                        <div className="text-xs uppercase text-gray-500">Yearly</div>
                        <div className="text-2xl font-bold text-gray-900">${tier.yearly_price}/year</div>
                        {tier.yearly_savings != null && (
                          <div className="text-xs text-green-600 mt-1">Save ${tier.yearly_savings}/yr</div>
                        )}
                      </div>
                      <div className="text-center p-3 rounded-lg bg-gray-50">
                        <div className="text-xs uppercase text-gray-500">Monthly</div>
                        <div className="text-2xl font-bold text-gray-900">${tier.monthly_price}/mo</div>
                      </div>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {(tier.features || []).map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center">
                          <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={handleGetStarted}
                      className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${tier.popular ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`}
                    >
                      Get Started
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add-ons */}
            {addons && addons.length > 0 && (
              <div className="mt-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Add-ons (optional)</h3>
                <div className="grid md:grid-cols-4 gap-6">
                  {addons.map((addon) => (
                    <div key={addon.id} className="border border-gray-200 rounded-xl p-6 shadow-sm bg-white">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">{addon.name}</h4>
                      <p className="text-sm text-gray-600 mb-4">{addon.description}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="text-center p-2 rounded bg-gray-50">
                          <div className="text-xs uppercase text-gray-500">Yearly</div>
                          <div className="text-xl font-bold text-gray-900">${addon.yearly_price}/yr</div>
                        </div>
                        <div className="text-center p-2 rounded bg-gray-50">
                          <div className="text-xs uppercase text-gray-500">Monthly</div>
                          <div className="text-xl font-bold text-gray-900">${addon.monthly_price}/mo</div>
                        </div>
                      </div>
                      {addon.quantity_based && (
                        <div className="mt-3 text-xs text-gray-500">Quantity-based add-on</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-indigo-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your HOA?</h2>
          <p className="text-xl mb-8 text-indigo-100">Join hundreds of communities already using HOA Manager Pro</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => onNavigate('setup-hoa')} className="bg-white text-indigo-600 hover:bg-gray-50 px-8 py-4 rounded-lg text-lg font-semibold">Start Free Trial</button>
            <button onClick={() => onNavigate('existing-login')} className="border-2 border-white text-white hover:bg-white hover:text-indigo-600 px-8 py-4 rounded-lg text-lg font-semibold">Community Login</button>
          </div>
          <p className="text-sm text-indigo-100 mt-4">
            💡 Use code: <span className="font-mono font-semibold bg-indigo-700 px-2 py-1 rounded">TRIAL-N1NFCEWRUJY</span> for extended trial access
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="/hoa-logo-icon.png" 
                  alt="HOA Manager Pro" 
                  className="h-12 w-12 object-contain"
                />
                <div className="ml-4 flex flex-col">
                  <span className="text-2xl font-bold tracking-wide" style={{ color: '#5A2D82', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                    HOA
                  </span>
                  <span className="text-sm font-bold tracking-wide leading-none" style={{ color: '#5A2D82', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                    MANAGER PRO
                  </span>
                </div>
              </div>
              <p className="text-gray-400">Simplifying HOA management for communities nationwide.</p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><button onClick={() => setShowPricing(true)} className="hover:text-white">Pricing</button></li>
                <li><button onClick={() => navigate('/features')} className="hover:text-white cursor-pointer">Features</button></li>
                <li><button onClick={() => navigate('/security')} className="hover:text-white cursor-pointer">Security</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/help')} className="hover:text-white cursor-pointer">Help Center</button></li>
                <li><button onClick={() => navigate('/contact')} className="hover:text-white cursor-pointer">Contact Us</button></li>
                <li><button onClick={() => navigate('/training')} className="hover:text-white cursor-pointer">Training</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/about')} className="hover:text-white cursor-pointer">About</button></li>
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white cursor-pointer">Privacy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white cursor-pointer">Terms</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="mb-3 text-indigo-300 italic">"Let all things be done decently and in order." - 1 Corinthians 14:40</p>
            <p>&copy; 2025 HOA Manager Pro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;